<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store.js';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import NewBoardDialog from './components/NewBoardDialog.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const boards = useMapGetter('kanban/getBoards');
const uiFlags = useMapGetter('kanban/getUIFlags');
const currentAccountId = useMapGetter('getCurrentAccountId');

const newBoardDialogRef = ref(null);

const sortedBoards = computed(() =>
  [...boards.value].sort((a, b) => {
    if (a.favorite !== b.favorite) return a.favorite ? -1 : 1;
    return a.name.localeCompare(b.name);
  })
);

onMounted(() => {
  store.dispatch('kanban/fetchBoards');
});

const openBoard = board => {
  router.push({
    name: 'kanban_board',
    params: { accountId: currentAccountId.value, boardId: board.id },
  });
};

const toggleFavorite = async board => {
  try {
    await store.dispatch('kanban/toggleFavorite', board.id);
  } catch (error) {
    useAlert(error.message);
  }
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header
      class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
    >
      <div class="flex items-center gap-2">
        <Icon icon="i-lucide-columns-3" class="size-5 text-n-slate-11" />
        <h1 class="text-lg font-medium text-n-slate-12">
          {{ t('KANBAN.OVERVIEW.TITLE') }}
        </h1>
      </div>
      <Button
        solid
        blue
        sm
        icon="i-lucide-plus"
        @click="newBoardDialogRef?.open()"
      >
        {{ t('KANBAN.OVERVIEW.NEW_BOARD') }}
      </Button>
    </header>

    <div class="flex-1 p-6 overflow-y-auto">
      <div
        v-if="!uiFlags.isFetchingBoards && !sortedBoards.length"
        class="flex flex-col items-center justify-center h-full gap-2 text-center"
      >
        <Icon icon="i-lucide-columns-3" class="size-10 text-n-slate-9" />
        <p class="text-base font-medium text-n-slate-12">
          {{ t('KANBAN.OVERVIEW.EMPTY_TITLE') }}
        </p>
        <p class="max-w-md text-sm text-n-slate-11">
          {{ t('KANBAN.OVERVIEW.EMPTY_DESCRIPTION') }}
        </p>
        <Button
          solid
          blue
          sm
          icon="i-lucide-plus"
          class="mt-2"
          @click="newBoardDialogRef?.open()"
        >
          {{ t('KANBAN.OVERVIEW.NEW_BOARD') }}
        </Button>
      </div>

      <div
        v-else
        class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      >
        <button
          v-for="board in sortedBoards"
          :key="board.id"
          class="flex flex-col gap-2 px-4 py-4 text-left border shadow-sm cursor-pointer bg-n-solid-1 rounded-xl border-n-weak hover:border-n-strong"
          @click="openBoard(board)"
        >
          <div class="flex items-center justify-between w-full gap-2">
            <span class="text-base font-medium truncate text-n-slate-12">
              {{ board.name }}
            </span>
            <span
              class="flex-shrink-0 p-1 rounded hover:bg-n-alpha-1"
              role="button"
              :title="t('KANBAN.OVERVIEW.FAVORITE')"
              @click.stop="toggleFavorite(board)"
            >
              <Icon
                :icon="board.favorite ? 'i-lucide-star' : 'i-lucide-star'"
                class="size-4"
                :class="board.favorite ? 'text-y-500' : 'text-n-slate-9'"
              />
            </span>
          </div>
          <p v-if="board.description" class="text-sm truncate text-n-slate-11">
            {{ board.description }}
          </p>
          <div class="flex items-center gap-3 mt-1 text-xs text-n-slate-10">
            <span class="flex items-center gap-1">
              <Icon icon="i-lucide-columns-3" class="size-3.5" />
              {{ board.steps_count }} {{ t('KANBAN.OVERVIEW.STEPS') }}
            </span>
            <span class="flex items-center gap-1">
              <Icon icon="i-lucide-square-check-big" class="size-3.5" />
              {{ board.tasks_count }} {{ t('KANBAN.OVERVIEW.TASKS') }}
            </span>
          </div>
        </button>
      </div>
    </div>

    <NewBoardDialog ref="newBoardDialogRef" @created="openBoard" />
  </section>
</template>
